// attribution/firstTouch.js
//
// Lightweight first-touch attribution. On the first visit we remember the UTM
// parameters, ad click ids, referrer and landing path in localStorage. It is
// never overwritten on later visits, survives the OAuth redirect/login, and is
// sent to the backend exactly once — when a signup completes. No PII.

const STORAGE_KEY = 'aluvo_first_touch';
const CAPTURED_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'ttclid', 'fbclid', 'gclid'];
const MAX_LEN = 200;

function clean(value) {
    if (value === null || value === undefined) return null;
    const str = String(value).trim();
    return str ? str.slice(0, MAX_LEN) : null;
}

function isOwnHost(referrer, ownHost) {
    try { return new URL(referrer).hostname.replace(/^www\./, '') === ownHost.replace(/^www\./, ''); } catch { return false; }
}

/** Build the attribution record for the current page (pure; exported for tests). */
export function buildFirstTouch({ href, referrer, ownHost, now = new Date() }) {
    let url;
    try { url = new URL(href); } catch { return null; }
    const record = {};
    for (const key of CAPTURED_KEYS) {
        const v = clean(url.searchParams.get(key));
        if (v) record[key] = v;
    }
    const ref = clean(referrer);
    if (ref && !isOwnHost(ref, ownHost)) record.referrer = ref;
    record.landing_path = clean(url.pathname) || '/';
    record.captured_at = now.toISOString();
    return record;
}

/** Store first-touch attribution if none is stored yet. Returns the stored record. */
export function captureFirstTouch(win = typeof window !== 'undefined' ? window : null) {
    if (!win) return null;
    try {
        const existing = win.localStorage.getItem(STORAGE_KEY);
        if (existing) return JSON.parse(existing);
    } catch { /* fall through and overwrite corrupt data */ }
    const record = buildFirstTouch({ href: win.location.href, referrer: win.document?.referrer || '', ownHost: win.location.hostname });
    if (!record) return null;
    try { win.localStorage.setItem(STORAGE_KEY, JSON.stringify(record)); } catch { /* storage blocked */ }
    return record;
}

export function getFirstTouch() {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch { return null; }
}

export function clearFirstTouch() {
    try { window.localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}
